import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, BadgeDollarSign, Plus } from 'lucide-react';
import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { api } from '../lib/api';
import { useI18n } from '../lib/i18n';
import { formatDate, formatMoney, isoDate } from '../lib/format';
import {
  Badge,
  Button,
  Card,
  CardHeader,
  EmptyState,
  ErrorState,
  Input,
  LoadingBlock,
  PageHeader,
  Table,
  Td,
  Th,
  Tr,
} from '../components/ui';

interface SalaryRow {
  id: string;
  fullName: string;
  fullNameAr: string | null;
  employeeCode: string | null;
  jobTitle: string | null;
  department: { id: string; code: string; name: string; nameAr: string | null } | null;
  current: { id: string; basicSalary: string; currency: string; effectiveFrom: string; effectiveTo: string | null } | null;
}

/**
 * Every active employee and the salary in force for them today (§19).
 *
 * This was the salary tab on the admin's employee page. Payroll moved to sys,
 * and salaries with it: the admin creates the employee, sys sets their pay.
 */
export function SalariesPage() {
  const { t, locale, localized } = useI18n();
  const navigate = useNavigate();
  const [q, setQ] = useState('');

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['salaries', q],
    queryFn: () => api.get<SalaryRow[]>('/payroll/salaries', { q: q.trim() || undefined }),
  });

  return (
    <>
      <PageHeader title={t('salaries.title')} subtitle={t('salaries.note')} count={data?.length} />

      <Card className="mb-4" padded={false}>
        <div className="p-4">
          <Input
            className="max-w-sm"
            placeholder={t('common.search')}
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
        </div>
      </Card>

      <Card padded={false}>
        {isLoading ? (
          <LoadingBlock />
        ) : error ? (
          <ErrorState error={error} onRetry={() => void refetch()} />
        ) : !data?.length ? (
          <EmptyState icon={<BadgeDollarSign className="h-8 w-8" />} />
        ) : (
          <Table>
            <thead>
              <tr>
                <Th>{t('access.employee')}</Th>
                <Th>{t('common.department')}</Th>
                <Th align="end">{t('salaries.current')}</Th>
                <Th align="end">{t('salaries.since')}</Th>
              </tr>
            </thead>
            <tbody>
              {data.map((row) => (
                <Tr key={row.id} onClick={() => navigate(`/salaries/${row.id}`)}>
                  <Td>
                    <span className="block text-[var(--text-primary)]">{localized(row)}</span>
                    <span className="block text-caption text-muted">
                      {[row.employeeCode, row.jobTitle].filter(Boolean).join(' · ') || '—'}
                    </span>
                  </Td>
                  <Td className="text-muted">{row.department ? localized(row.department) : '—'}</Td>
                  <Td align="end" className="tabular">
                    {row.current ? (
                      formatMoney(row.current.basicSalary, row.current.currency, locale)
                    ) : (
                      <Badge tone="warning">{t('salaries.none')}</Badge>
                    )}
                  </Td>
                  <Td align="end" className="tabular text-muted">
                    {row.current ? formatDate(row.current.effectiveFrom, locale) : '—'}
                  </Td>
                </Tr>
              ))}
            </tbody>
          </Table>
        )}
      </Card>
    </>
  );
}

/** One employee's salary records, newest first. Route: `/salaries/:userId`. */
export function SalaryHistoryPage() {
  const { userId = '' } = useParams();
  const { t, locale, localized } = useI18n();
  const navigate = useNavigate();

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['salaries', 'history', userId],
    queryFn: () => api.get<any>(`/payroll/salaries/${userId}`),
  });

  if (isLoading) return <LoadingBlock />;
  if (error) return <ErrorState error={error} onRetry={() => void refetch()} />;

  const { employee, profiles } = data;
  // Open-ended and already started: that is the record payroll uses today. An
  // open-ended one that starts later is scheduled, not current.
  const today = isoDate();
  const inForce = (p: any) => !p.effectiveTo && String(p.effectiveFrom).slice(0, 10) <= today;

  return (
    <>
      <div className="mb-4">
        <Link to="/salaries" className="inline-flex items-center gap-1.5 text-body-sm text-muted hover:text-[var(--text-primary)]">
          <ArrowLeft className="h-4 w-4 flip-rtl" aria-hidden />
          {t('salaries.title')}
        </Link>
      </div>

      <PageHeader
        title={localized(employee)}
        subtitle={[
          employee.employeeCode,
          employee.department ? localized(employee.department) : null,
          employee.jobTitle,
        ]
          .filter(Boolean)
          .join(' · ')}
        action={
          employee.isActive && (
            <Button
              variant="primary"
              icon={<Plus className="h-4 w-4" />}
              onClick={() => navigate(`/salaries/${userId}/new`)}
            >
              {t('salaries.newRecord')}
            </Button>
          )
        }
      />

      <Card padded={false}>
        <CardHeader title={t('employees.salaryHistory')} subtitle={t('salaries.recordNote')} className="p-5 pb-0" />
        {!profiles.length ? (
          <EmptyState title={t('salaries.none')} hint="" />
        ) : (
          <Table>
            <thead>
              <tr>
                <Th>{t('employees.effectiveFrom')}</Th>
                <Th>{t('salaries.until')}</Th>
                <Th align="end">{t('employees.basicSalary')}</Th>
                <Th align="end">{t('payroll.overtime')}</Th>
                <Th align="end">{t('payroll.nightAllowance')}</Th>
                <Th align="end">{t('payroll.otherAllowances')}</Th>
              </tr>
            </thead>
            <tbody>
              {profiles.map((p: any) => (
                <Tr key={p.id}>
                  <Td className="tabular">{formatDate(p.effectiveFrom, locale)}</Td>
                  <Td className="tabular text-muted">
                    {p.effectiveTo ? (
                      formatDate(p.effectiveTo, locale)
                    ) : inForce(p) ? (
                      <Badge tone="success">{t('salaries.current')}</Badge>
                    ) : (
                      '—'
                    )}
                  </Td>
                  <Td align="end" className="tabular">{formatMoney(p.basicSalary, p.currency, locale)}</Td>
                  <Td align="end" className="tabular text-muted">×{Number(p.overtimeMultiplier ?? 0)}</Td>
                  <Td align="end" className="tabular text-muted">
                    {formatMoney(p.nightShiftAllowanceValue, p.currency, locale)}
                  </Td>
                  <Td align="end" className="tabular text-muted">
                    {formatMoney(p.otherAllowances, p.currency, locale)}
                  </Td>
                </Tr>
              ))}
            </tbody>
          </Table>
        )}
      </Card>
    </>
  );
}
